// Unit control on the battlefield (2026-10-26, branch controls; the scheme, the research behind it and what newcomers
// see: docs/CONTROLS.md). Selecting is the client's alone; every order it gives is a sim command (sim.ts 'order',
// 'group', 'rally') so a networked room can replay it.
//
// Nothing selected means the whole army: a tap sends it (the army goal), as before. Hold a beat and drag to draw a box
// round soldiers; hold and let go on one to pick every soldier of its kind on screen; tap your hero's portrait for the
// hero. With a selection, a tap (or a goal button) orders only them. Four numbered group buttons: an empty one with a
// selection takes it, a full one picks its soldiers again (twice quickly: the camera goes to them), holding one opens
// its card (make it the selection, add to it, new soldiers join it, back to the army). Keys: docs/CONTROLS.md.
import { CLASSES, HERO } from '../../sim/defs';
import { CAP, GROUPS, STATE, type Command, type Goal, type Sim } from '../../sim/sim';
import { CLASS_LABEL, TEAM_NAME } from '../ui/data';
import { Gestures, type Act } from './gesture';

type Tone = 'info' | 'good' | 'warn';
export type View = {
  guiding?: () => boolean;
  /** A guide step done, or 'group': a group's card opened by a hold (the group tip's action, tips.ts). */
  learned?: (action: 'send' | 'select' | 'retreat' | 'group') => void;
  sim: () => Sim; me: () => number;
  /** Where unit i was drawn this frame (x, z): between its last two steps. */
  at: (i: number) => [number, number];
  /** Whether you can give orders now (your match, on the play screen, still in). */
  live: () => boolean;
  send: (c: Command) => boolean;
  /** A ground point to CSS px (null behind the camera); a CSS px point to the ground (null off the table). */
  toScreen: (x: number, z: number) => { x: number; y: number } | null;
  toGround: (sx: number, sy: number) => { x: number; z: number } | null;
  /** A drag by dx, dy ending at x, y; a pinch times k about its midpoint x, y, which moved by dx, dy (CSS px). */
  pan: (dx: number, dy: number, x: number, y: number) => void; zoom: (k: number, x: number, y: number, dx: number, dy: number) => void; jump: (x: number, z: number) => void;
  say: (text: string, secs?: number, tone?: Tone) => void;
  /** The army's own goal, as a tap with nothing selected always sent it (the toast included). */
  armyGoal: (g: Goal) => void;
  /** The order marker on the ground: 'fight' (attack-move, gold) or 'move' (walk past fights, ivory). */
  mark: (x: number, z: number, kind: 'fight' | 'move') => void;
  /** A card over the bar (the HUD's sheet) and its close. */
  panel: (title: string, body: HTMLElement, note?: string) => void; closePanel: () => void;
};

/** How near (CSS px) a press or click must land to a soldier to pick him: a finger covers about 40 px. */
export const PICK_PX = 30, CLICK_PX = 16;
/** Hold a group button this long (ms) for its card. Two presses of a group this close (ms) move the camera to it. */
export const CARD_MS = 450;
const JUMP_MS = 400;
/** The finger that held a card open lifts on whatever the card put under it: on a phone with the slot row folded,
 *  group 1's card puts its Close there, and the lift closed the card it had just opened (uishots c5, 2026-10-30).
 *  That lift is not a tap, so its click is eaten, and only that one (2026-10-31, tinwhistlegames review F4: a half
 *  second window after any lift ate real taps). The eater waits for pointer `id` itself to lift (a second finger's lift
 *  doesn't count), then takes the first click, or goes at the next press or key when the lift made no click (a hold
 *  past the browser's own long press). `lifted` runs when pointer `id` lifts. `root` is the window (a test's target).
 *  Only a real pointer's click from `id` is eaten (2026-10-31, Flash F1): a click with detail 0 (Enter, Space, a screen
 *  reader, Switch or Voice Control) or another finger's goes through, and a cancelled pointer or the window losing focus
 *  mid-hold (no click can follow either) ends the eater at once. */
export function eatLift(id: number, lifted: () => void, root: EventTarget = globalThis as unknown as EventTarget) {
  let up = false;
  const eat = (e: Event) => {
    const c = e as PointerEvent; // Safari's click has no pointerId: then any real pointer's click is the lift's
    if (!up || c.detail === 0 || (typeof c.pointerId === 'number' && c.pointerId !== id)) return;
    e.stopPropagation(); e.preventDefault(); stop();
  };
  const lift = (e: Event) => { if (up || (e as PointerEvent).pointerId !== id) return; up = true; lifted(); };
  const end = () => { stop(); if (!up) { up = true; lifted(); } };
  const cancel = (e: Event) => { if ((e as PointerEvent).pointerId === id) end(); };
  const blur = (e: Event) => { if (e.target === root) end(); }; // the window's own blur, not an element's
  const next = () => { if (up) stop(); };
  const on = [['click', eat], ['pointerup', lift], ['pointercancel', cancel], ['blur', blur], ['pointerdown', next], ['keydown', next]] as const;
  const cap = { capture: true }; // an object, not `true`: Node's EventTarget (the tests) ignores a bare true on remove
  const stop = () => { for (const [t, f] of on) root.removeEventListener(t, f, cap); };
  for (const [t, f] of on) root.addEventListener(t, f, cap);
}

/** A tap on `b` does fn. Holding it down with a finger or the main mouse button does long, and the lift's click is
 *  eaten (eatLift). A right click, Android's long-press menu or the menu key do long through contextmenu, which no
 *  click follows, so nothing is eaten for them; one hold opens the card once. Only the holding pointer's own lift or
 *  leave ends its hold (2026-10-31, Flash F2: a second finger brushing the button ended the first's), and a long-press
 *  menu that comes before the timer (Android's short touch-and-hold setting, 400 ms) opens the card as the timer would,
 *  eater and all, since that finger is still down. */
export function hold(b: EventTarget, fn: () => void, long: () => void, root?: EventTarget) {
  let timer: ReturnType<typeof setTimeout> | undefined, held = false; // held: this hold opened the card, its pointer still down
  let down: number | null = null; // the pointer holding the button, until its hold ends or opens the card
  const card = (id: number) => { clearTimeout(timer); down = null; held = true; long(); eatLift(id, () => { held = false; }, root); };
  b.addEventListener('pointerdown', (e) => {
    const p = e as PointerEvent;
    if (p.button !== 0) return; // a right click opens the card through contextmenu
    clearTimeout(timer); down = p.pointerId;
    timer = setTimeout(() => card(p.pointerId), CARD_MS);
  });
  const end = (e: Event) => { if ((e as PointerEvent).pointerId === down) { clearTimeout(timer); down = null; } };
  for (const ev of ['pointerup', 'pointerleave', 'pointercancel']) b.addEventListener(ev, end);
  b.addEventListener('click', fn);
  b.addEventListener('contextmenu', (e) => { e.preventDefault(); clearTimeout(timer); if (held) return; if (down !== null) card(down); else long(); });
}

/** What a tap on the ground asks for: a base attacks it (yours: defend), the center holds it, anywhere else rallies. */
export function goalAt(s: Sim, x: number, z: number): Goal {
  const onBase = s.bases.findIndex((b) => Math.hypot(b.x - x, b.y - z) < 7);
  return onBase >= 0 ? { kind: 'base', p: onBase } : Math.hypot(x - s.center.x, z - s.center.y) < 8 ? { kind: 'center' } : { kind: 'point', x, y: z };
}
/** The group a key stands for, by the key's place and not its letter (Shift+1 types "!" on a US keyboard, Astra F4),
 *  else -1. */
export function groupKey(code: string) { const d = /^(Digit|Numpad)([1-9])$/.exec(code), k = d ? Number(d[2]) - 1 : -1; return k < GROUPS ? k : -1; }
/** Indices of the points inside a screen rectangle (any corner order). */
export function inRect(pts: { i: number; x: number; y: number }[], x0: number, y0: number, x1: number, y1: number) {
  const [l, r, t, b] = [Math.min(x0, x1), Math.max(x0, x1), Math.min(y0, y1), Math.max(y0, y1)];
  return pts.filter((p) => p.x >= l && p.x <= r && p.y >= t && p.y <= b).map((p) => p.i);
}
/** The point nearest (x, y) within r, else -1. */
export function nearest(pts: { i: number; x: number; y: number }[], x: number, y: number, r: number) {
  let best = -1, bd = r * r;
  for (const p of pts) { const d = (p.x - x) ** 2 + (p.y - y) ** 2; if (d <= bd) { bd = d; best = p.i; } }
  return best;
}

const h = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };
const BOX_ICON = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M3 3h5v2H5v3H3zm13 0h5v5h-2V5h-3zM3 16h2v3h3v2H3zm16 0h2v5h-5v-2h3z" fill="currentColor"/><circle cx="9.5" cy="12" r="2.2" fill="currentColor"/><circle cx="14.5" cy="12" r="2.2" fill="currentColor"/></svg>';

type GroupBtn = { b: HTMLButtonElement; n: HTMLElement; sig: string };

export class Controls {
  readonly gestures: Gestures;
  /** Per unit slot: 1 selected, 2 inside the box being drawn (the team disc lights for both). */
  readonly lit = new Uint8Array(CAP);
  /** Selected slots and the uid each held when picked: a slot whose uid moved on is someone new, and drops out. */
  private sel = new Map<number, number>();
  private box = h('div', 'selbox');
  private chip!: HTMLButtonElement;
  private fanBtn!: HTMLButtonElement;
  private fanN!: HTMLElement;
  private btns: GroupBtn[] = [];
  private preview: number[] = [];
  private lastGroup = { k: -1, t: -1e9 };
  private lastHero = -1e9;
  private hints = 0;

  constructor(readonly v: View, readonly mount: HTMLElement) {
    this.gestures = new Gestures((a) => this.act(a));
    this.box.hidden = true; this.box.setAttribute('aria-hidden', 'true');
    document.body.append(this.box);
    // capture, so Escape with a selection clears it before the pause screen sees the key
    addEventListener('keydown', (e) => { if (this.key(e)) { e.preventDefault(); e.stopImmediatePropagation(); } }, true);
    this.build();
  }

  /** A new match: nothing selected, and the buttons for your seat (none when watching). */
  setMatch() { this.clear(); this.build(); }

  get count() { return this.sel.size; }
  /** The selected units' uids, in slot order. */
  ids() { return [...this.sel.keys()].sort((a, b) => a - b).map((i) => this.v.sim().uid[i]); }
  /** What the goal line says while something is selected, else null (it says the army's goal). */
  target() {
    if (!this.sel.size) return null;
    const k = this.groupIs();
    return k >= 0 ? `Group ${k + 1}: ${this.sel.size}` : `${this.sel.size} selected`;
  }

  /** A goal button (Center, Defend, Attack's card) with a selection orders the selection; Defend pulls it home without
   *  stopping on the way (it fights again near home). True when it did. */
  goal(g: Goal) {
    this.fold();
    if (!this.sel.size || !this.v.live()) return false;
    const home = g.kind === 'base' && g.p === this.v.me();
    this.order(g, home);
    return true;
  }

  /** Every frame: drop the fallen from the selection, light the discs, keep the buttons current. */
  update(now: number) {
    this.gestures.tick(now);
    const s = this.v.sim(), me = this.v.me();
    for (const [i, u] of this.sel) if (s.state[i] === STATE.dead || s.uid[i] !== u || s.team[i] !== me) this.sel.delete(i);
    this.lit.fill(0);
    for (const i of this.preview) this.lit[i] = 2;
    for (const i of this.sel.keys()) this.lit[i] = 1;
    if (me < 0 || !this.btns.length) return;
    const n = new Array(GROUPS).fill(0);
    for (let i = 0; i < CAP; i++) if (s.state[i] !== STATE.dead && s.team[i] === me && s.squad[i] >= 0) n[s.squad[i]]++;
    const pl = s.players[me], on = this.groupIs();
    this.btns.forEach((g, k) => {
      const sig = `${n[k]}|${pl.rally === k}|${on === k}`;
      if (g.sig === sig) return;
      g.sig = sig;
      g.n.textContent = n[k] ? String(n[k]) : '';
      g.b.dataset.empty = String(!n[k]); g.b.dataset.rally = String(pl.rally === k);
      g.b.setAttribute('aria-pressed', String(on === k));
      g.b.setAttribute('aria-label', `Group ${k + 1}: ${n[k] ? `${n[k]} soldiers` : 'empty'}${pl.rally === k ? ', new soldiers join it' : ''}. Hold for more`);
    });
    const c = this.chip, cs = `${this.sel.size}|${this.gestures.armed}`;
    if (c.dataset.sig !== cs) {
      c.dataset.sig = cs;
      if (this.sel.size) { c.innerHTML = ''; c.append(h('b', 'gn', String(this.sel.size)), h('span', 'x', '×')); c.setAttribute('aria-label', `${this.sel.size} selected. Clear`); c.setAttribute('aria-pressed', 'false'); }
      else { c.innerHTML = BOX_ICON; c.setAttribute('aria-label', 'Select soldiers: drag a box on the map'); c.setAttribute('aria-pressed', String(this.gestures.armed)); }
      c.title = this.sel.size ? 'Clear the selection (Esc)' : 'Draw a box round soldiers to select them. Or hold a finger on the map, then drag';
    }
    this.fan(this.mount.classList.contains('fanned'));
  }

  /** For ?qa=1 (__qa.ctl): what is selected, each group's size, where recruits go, whether a box is up. */
  qa() {
    const s = this.v.sim(), me = this.v.me();
    return { sel: this.sel.size, hero: me >= 0 && [...this.sel.keys()].some((i) => s.heroOf[i] === me), lit: this.lit.reduce((a, b) => a + (b ? 1 : 0), 0),
      groups: me < 0 ? [] : Array.from({ length: GROUPS }, (_, k) => this.members(k).length), rally: me >= 0 ? s.players[me].rally : -1,
      armed: this.gestures.armed, box: !this.box.hidden, ords: me < 0 ? 0 : [...this.sel.keys()].filter((i) => s.ord[i] >= 0).length,
      goal: me >= 0 ? s.players[me].goal.kind : null, moving: me < 0 ? 0 : [...this.sel.keys()].filter((i) => s.moving[i]).length };
  }

  // ---- input
  private act(a: Act) {
    const v = this.v;
    if (a.a === 'pan') { v.pan(a.dx, a.dy, a.x, a.y); return; }
    if (a.a === 'pinch') { v.zoom(a.k, a.x, a.y, a.dx, a.dy); return; }
    if (a.a === 'cancel') { this.preview = []; this.box.hidden = true; return; }
    if (!v.live()) { this.box.hidden = true; this.preview = []; return; }
    if (a.a === 'hold') { this.drawBox(a.x, a.y, a.x, a.y, true); navigator.vibrate?.(8); return; }
    if (a.a === 'box') {
      const inside = inRect(this.mine(), a.x0, a.y0, a.x1, a.y1);
      if (!a.done) { this.drawBox(a.x0, a.y0, a.x1, a.y1, false); this.preview = inside; return; }
      this.box.hidden = true; this.preview = [];
      this.select(inside, a.shift, 'box');
      if (inside.some(i => v.sim().heroOf[i] < 0)) v.learned?.('select');
      return;
    }
    if (a.a === 'press') {
      this.box.hidden = true;
      const i = nearest(this.mine(), a.x, a.y, PICK_PX);
      if (i < 0) { this.tapGround(a.x, a.y, false); return; } // a slow tap is still a tap
      this.pickKind(i, a.shift);
      return;
    }
    // a tap: a click on one of your soldiers picks him (a double click, his kind); otherwise the ground gets an order
    if (a.mouse && a.button === 0) {
      const i = nearest(this.mine(), a.x, a.y, CLICK_PX);
      if (i >= 0) { if (a.double) this.pickKind(i, a.shift); else this.pickOne(i, a.shift); return; }
    }
    this.tapGround(a.x, a.y, a.mouse && a.alt);
  }

  private tapGround(sx: number, sy: number, move: boolean) {
    const p = this.v.toGround(sx, sy);
    if (!p) return;
    this.fold();
    const g = goalAt(this.v.sim(), p.x, p.z);
    if (this.sel.size) this.order(g, move || (g.kind === 'base' && g.p === this.v.me()), p);
    else { this.v.armyGoal(g); this.v.mark(p.x, p.z, 'fight'); }
    this.v.learned?.('send');
  }

  /** Order the selection to g: attack-move, or (move) walking past fights until there. */
  private order(g: Goal, move: boolean, at?: { x: number; z: number }) {
    this.fold(); // R included (Flash re-check F3)
    const v = this.v, s = v.sim(), ids = this.ids(), k = this.groupIs();
    if (!v.send({ t: 'order', ids, goal: g, move, ...(at ? { tap: { x: at.x, y: at.z } } : {}) })) { v.say('Rejoin the army before giving more orders', 3, 'warn'); return; }
    const selected = Array.from({ length: CAP }, (_, i) => i).filter(i => s.state[i] !== STATE.dead && ids.includes(s.uid[i]));
    if (selected.length) g = s.goalOf(selected[0]);
    const retreating = move ? 0 : selected.filter(i => s.moving[i] === 1).length;
    const spot = at ?? (g.kind === 'base' ? { x: s.bases[g.p].x, z: s.bases[g.p].y } : g.kind === 'center' ? { x: s.center.x, z: s.center.y } : { x: g.x, z: g.y });
    if (move || retreating) v.learned?.('retreat');
    v.mark(spot.x, spot.z, move || retreating ? 'move' : 'fight');
    const many = k < 0 && ids.length > 1, who = k >= 0 ? `Group ${k + 1}` : many ? `${ids.length} soldiers` : 'One soldier', me = v.me();
    v.say(retreating ? `${who} to the flag (${retreating} disengaging)` : move ? `${who} ${many ? 'move' : 'moves'} to the flag without stopping` : g.kind === 'center' ? `${who} to the center` : g.kind === 'base' ? (g.p === me ? `${who} to defend home` : `${who} ${many ? 'march' : 'marches'} on ${TEAM_NAME[g.p]}`) : `${who} to the flag`, 2);
  }

  /** Your units on screen with their kind, for the UI walk. */
  onScreen() { const s = this.v.sim(); return this.mine().map((p) => ({ ...p, cls: s.cls[p.i], hero: s.heroOf[p.i] >= 0 })); }
  /** Your living units on screen, as CSS px points. */
  private mine() {
    const s = this.v.sim(), me = this.v.me(), out: { i: number; x: number; y: number }[] = [];
    if (me < 0) return out;
    for (let i = 0; i < CAP; i++) {
      if (s.state[i] === STATE.dead || s.team[i] !== me) continue;
      const [x, z] = this.v.at(i), p = this.v.toScreen(x, z);
      if (p && p.x >= 0 && p.y >= 0 && p.x <= innerWidth && p.y <= innerHeight) out.push({ i, x: p.x, y: p.y });
    }
    return out;
  }

  private select(is: number[], add: boolean, how: 'box' | 'kind' | 'one' | 'group' | 'hero', label = '') {
    const s = this.v.sim();
    if (!add) this.sel.clear();
    for (const i of is) this.sel.set(i, s.uid[i]);
    if (!is.length && how === 'box' && !add) return;
    // the first few times a session, say what a selection does (a control nobody is told about goes unused)
    if (this.v.guiding?.()) return;
    if (this.hints < 2 && this.sel.size && how !== 'group') { this.hints++; this.v.say(`Tap the map to send selected soldiers`, 4); }
    else if (label) this.v.say(label, 1.5);
  }
  private pickOne(i: number, add: boolean) {
    if (add && this.sel.has(i)) { this.sel.delete(i); return; }
    this.select([i], add, 'one');
  }
  /** Every soldier of unit i's kind on screen (the hero alone for the hero). */
  private pickKind(i: number, add: boolean) {
    const s = this.v.sim();
    if (s.heroOf[i] >= 0) { this.select([i], add, 'hero', HERO[s.players[s.heroOf[i]].hero!.id].short); return; }
    const c = s.cls[i], all = this.mine().map((p) => p.i).filter((j) => s.heroOf[j] < 0 && s.cls[j] === c);
    this.select(all, add, 'kind', `${all.length} ${CLASS_LABEL[CLASSES[c]]}`);
  }
  selectHero() {
    const s = this.v.sim(), me = this.v.me(), i = me >= 0 ? s.players[me].hero?.i ?? -1 : -1;
    if (i < 0 || !this.v.live()) return;
    const now = performance.now();
    if (this.sel.size === 1 && this.sel.has(i) && now - this.lastHero < JUMP_MS) this.v.jump(s.x[i], s.y[i]);
    this.lastHero = now;
    this.select([i], false, 'hero');
  }
  clear() { this.sel.clear(); this.preview = []; this.box.hidden = true; this.gestures.armed = false; }

  private drawBox(x0: number, y0: number, x1: number, y1: number, armed: boolean) {
    const b = this.box, l = Math.min(x0, x1), t = Math.min(y0, y1);
    b.hidden = false; b.classList.toggle('armed', armed);
    b.style.transform = `translate(${l}px, ${t}px)`;
    b.style.width = `${Math.abs(x1 - x0)}px`; b.style.height = `${Math.abs(y1 - y0)}px`;
  }

  // ---- groups
  /** Group k's living members (slots). */
  private members(k: number) {
    const s = this.v.sim(), me = this.v.me(), out: number[] = [];
    for (let i = 0; i < CAP; i++) if (s.state[i] !== STATE.dead && s.team[i] === me && s.squad[i] === k) out.push(i);
    return out;
  }
  /** The group the selection is exactly, else -1. */
  private groupIs() {
    if (!this.sel.size) return -1;
    const s = this.v.sim(), first = s.squad[this.sel.keys().next().value!];
    if (first < 0) return -1;
    for (const i of this.sel.keys()) if (s.squad[i] !== first) return -1;
    return this.members(first).length === this.sel.size ? first : -1;
  }
  /** A tap on group k: recall it (twice quickly, the camera goes there), or with a selection and k empty, make it k. */
  tapGroup(k: number) {
    const v = this.v;
    if (!v.live()) return;
    const ms = this.members(k), now = performance.now();
    if (ms.length) {
      if (this.lastGroup.k === k && now - this.lastGroup.t < JUMP_MS) { const s = v.sim(); v.jump(ms.reduce((a, i) => a + s.x[i], 0) / ms.length, ms.reduce((a, i) => a + s.y[i], 0) / ms.length); }
      this.lastGroup = { k, t: now };
      this.select(ms, false, 'group');
      return;
    }
    if (!this.sel.size) { this.show(); v.say(`Select soldiers, then tap ${k + 1} for group ${k + 1}`, 4); return; }
    this.setGroup(k, false);
  }
  setGroup(k: number, add: boolean) {
    const v = this.v;
    if (!this.sel.size || !v.live()) return;
    if (!v.send({ t: 'group', k, ids: this.ids(), add })) return;
    this.show(); // the toast says to tap k (Ctrl+k, or a card's button, can set a group while the phone's fan is shut)
    v.say(`Group ${k + 1}: ${add ? this.members(k).length : this.sel.size} soldiers. Tap ${k + 1} to select.`, 3, 'good');
  }
  /** Group k's card: make it the selection, add the selection, new soldiers join it, back to the army. */
  openCard(k: number) {
    const v = this.v, s = v.sim(), me = v.me();
    if (!v.live()) return;
    const ms = this.members(k), n = this.sel.size, rally = s.players[me].rally === k;
    v.learned?.('group');
    const grid = h('div', 'choices');
    const item = (title: string, line: string, fn: () => void, pressed?: boolean) => {
      const b = h('button', 'plaque choice plain'), words = h('span', 'words');
      words.append(h('b', '', title), h('small', 'role', line)); b.append(words);
      if (pressed !== undefined) b.setAttribute('aria-pressed', String(pressed));
      b.addEventListener('click', () => { fn(); v.closePanel(); });
      grid.append(b);
    };
    if (n && this.groupIs() !== k) item(`Make these ${n} group ${k + 1}`, ms.length ? `In place of the ${ms.length} in it now` : 'Tap the number to select', () => this.setGroup(k, false));
    if (n && ms.length && this.groupIs() !== k) item(`Add these ${n} to group ${k + 1}`, `It has ${ms.length}`, () => this.setGroup(k, true));
    item(rally ? 'New soldiers join the army' : `New soldiers join group ${k + 1}`, rally ? `They join group ${k + 1} now` : 'Send recruits with this group',
      () => { if (v.send({ t: 'rally', k: rally ? -1 : k })) v.say(rally ? 'New soldiers join the army' : `New soldiers join group ${k + 1}`, 2, 'good'); }, rally);
    // who is in the group when the button is pressed, not when the card opened: the match runs on under it (Astra F2)
    if (ms.length) item('Back to the army', `Group ${k + 1} follows the army's orders again`, () => {
      const now = this.members(k);
      if (now.length && v.send({ t: 'order', ids: now.map((i) => s.uid[i]), goal: null })) { this.fold(); v.say(`Group ${k + 1} rejoins the army`, 2); }
    });
    v.panel(`Group ${k + 1}`, grid, ms.length ? `${ms.length} soldiers.` : 'Empty.');
  }

  private build() {
    this.mount.replaceChildren(); this.btns = [];
    if (this.v.me() < 0) return;
    for (let k = 0; k < GROUPS; k++) {
      const b = h('button', 'plaque group'), n = h('small', 'gc');
      b.append(h('b', 'gn', String(k + 1)), n);
      b.title = `Group ${k + 1}: tap to select or save soldiers. Hold for more. Keys ${k + 1}, Ctrl+${k + 1}`;
      hold(b, () => this.tapGroup(k), () => this.openCard(k));
      this.mount.append(b); this.btns.push({ b, n, sig: '' });
    }
    this.chip = h('button', 'plaque group selchip');
    this.chip.addEventListener('click', () => { if (this.sel.size) this.clear(); else this.gestures.armed = !this.gestures.armed; });
    this.mount.append(this.chip);
    this.chip.dataset.sig = '';
    // 2026-10-31 (HUD take C): on a portrait phone groups 1 to 4 fold behind this one button (index.html .gtoggle), so the
    // right edge, where enemies walk in, holds two buttons instead of five. Other layouts hide it and show the row as before.
    // The fan stays open while you pick, set a group or use its card, and shuts when you give an order (a tap on the
    // ground or a goal button), on Esc, or on its own button (review of feat/hud-bc F4: a fold on any press hid the group
    // a card had just named in its toast).
    this.fanBtn = h('button', 'plaque group gtoggle');
    this.fanN = h('b', 'gn');
    this.fanBtn.append(this.fanN, h('small', 'gl', 'Groups'));
    this.fanBtn.setAttribute('aria-controls', this.mount.id = 'hud-groups');
    this.fanBtn.addEventListener('click', (e) => { this.fan(!this.mount.classList.contains('fanned')); if (e.detail === 0 && this.btns[0]) this.btns[0].b.focus(); });
    this.mount.append(this.fanBtn);
    this.fan(false);
  }
  /** Open or shut the phone's group fan; the button names the selected group while it is shut. Its words change in
   *  place: update() calls this every frame and the selected group changes under a press (a recruit joins it, a member
   *  falls), and a button whose contents are rebuilt mid-press can lose the tap (review of feat/hud-bc F3). */
  private fan(on: boolean) {
    const k = this.groupIs(), sel = k >= 0 ? String(k + 1) : '';
    if (this.fanBtn.dataset.sig === `${on}|${sel}`) return;
    this.fanBtn.dataset.sig = `${on}|${sel}`;
    this.mount.classList.toggle('fanned', on);
    this.fanBtn.setAttribute('aria-expanded', String(on));
    this.fanN.textContent = sel || '1-4';
    this.fanBtn.setAttribute('aria-label', `Groups${sel ? `, group ${sel} selected` : ''}. ${on ? 'Hide' : 'Show'} groups 1 to 4`);
  }
  /** An order was given: the fan has done its job. */
  private fold() { if (this.fanBtn && this.mount.classList.contains('fanned')) this.fan(false); }
  /** A toast names a group button: on a phone with the fan shut, open it so the button is there to tap. */
  private show() { if (this.fanBtn?.checkVisibility()) this.fan(true); }
  // ---- keys
  /** 1 to 4 pick a group (twice: the camera goes there), Ctrl sets it, Shift adds to it; H your hero; R pulls the
   *  selection home; Esc clears it. True when the key was ours. */
  key(e: KeyboardEvent) {
    if (!this.v.live() || (e.repeat && !/^(Digit|Numpad)[1-9]$/.test(e.code))) return false;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return false;
    const k = groupKey(e.code);
    if (k >= 0) {
      if (e.repeat) return true;
      if (e.ctrlKey || e.metaKey) this.setGroup(k, false); else if (e.shiftKey) this.setGroup(k, true); else this.tapGroup(k);
      return true;
    }
    const key = e.key.toLowerCase();
    // Esc folds the groups first, but only where they fold (landscape shows them all, so Esc goes on to the selection)
    if (key === 'escape' && this.mount.classList.contains('fanned') && this.fanBtn.checkVisibility()) { this.fan(false); this.fanBtn.focus(); return true; }
    if (key === 'escape' && this.sel.size) { this.clear(); return true; }
    if (key === 'h' && !e.ctrlKey && !e.metaKey) { this.selectHero(); return true; }
    if (key === 'r' && !e.ctrlKey && !e.metaKey && this.sel.size) { this.order({ kind: 'base', p: this.v.me() }, true); return true; }
    return false;
  }
}
